import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import {
  consolidateIdenticalPrimaryBlocks,
  type DtoolsReviewPrimary,
  type DtoolsReviewSystem,
} from "./dtoolsStagingTransform";
import { classifyDtoolsComponentTier } from "./dtoolsComponentClassification";
import { productPrimaryBlocks } from "./dtoolsReviewNavigation";

export type DtoolsOptionComponentGroups = {
  primaryLines: DtoolsStagingLine[];
  supportingLines: DtoolsStagingLine[];
  primaryTotal: number;
  supportingTotal: number;
  combinedTotal: number;
};

function flattenProductLines(blocks: DtoolsReviewPrimary[]): DtoolsStagingLine[] {
  const out: DtoolsStagingLine[] = [];
  for (const block of productPrimaryBlocks(blocks)) {
    out.push(block.line);
    for (const accessory of block.accessories) {
      out.push(accessory);
    }
  }
  return out;
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Group all billable product lines in an option into primary vs supporting tiers. */
export function buildOptionComponentGroups(system: DtoolsReviewSystem): DtoolsOptionComponentGroups {
  const allLines: DtoolsStagingLine[] = [];
  for (const room of system.rooms) {
    allLines.push(...flattenProductLines(room.primaries));
  }
  allLines.push(...flattenProductLines(system.systemLevelPrimaries));

  const primaryLines: DtoolsStagingLine[] = [];
  const supportingLines: DtoolsStagingLine[] = [];

  for (const line of allLines) {
    if (classifyDtoolsComponentTier(line) === "SUPPORTING_COMPONENT") {
      supportingLines.push(line);
    } else {
      primaryLines.push(line);
    }
  }

  const primaryTotal = roundMoney(primaryLines.reduce((s, l) => s + roundMoney(Number(l.lineTotal) || 0), 0));
  const supportingTotal = roundMoney(
    supportingLines.reduce((s, l) => s + roundMoney(Number(l.lineTotal) || 0), 0),
  );

  return {
    primaryLines,
    supportingLines,
    primaryTotal,
    supportingTotal,
    combinedTotal: roundMoney(primaryTotal + supportingTotal),
  };
}

/** Primary component blocks — nesting only from D-Tools parentId (plus saved hierarchy overrides on lines). */
export function collectOptionPrimaryComponentBlocks(system: DtoolsReviewSystem): DtoolsReviewPrimary[] {
  const blocks: DtoolsReviewPrimary[] = [];
  for (const room of system.rooms) {
    blocks.push(...productPrimaryBlocks(room.primaries));
  }
  blocks.push(...productPrimaryBlocks(system.systemLevelPrimaries));
  return consolidateIdenticalPrimaryBlocks(blocks);
}

/** Dedupe sort for stable presentation — brand/model first. */
export function sortComponentLinesForDisplay(lines: DtoolsStagingLine[]): DtoolsStagingLine[] {
  return [...lines].sort((a, b) => {
    const labelA = [a.manufacturer, a.model].filter(Boolean).join(" ") || a.polishedDescription || a.dtoolsDescription;
    const labelB = [b.manufacturer, b.model].filter(Boolean).join(" ") || b.polishedDescription || b.dtoolsDescription;
    return labelA.localeCompare(labelB, undefined, { sensitivity: "base" });
  });
}

/** PDF primary blocks — only lines classified as primary tier (no cable/accessory duplication). */
export function collectPdfPrimaryComponentBlocks(system: DtoolsReviewSystem): DtoolsReviewPrimary[] {
  const groups = buildOptionComponentGroups(system);
  const primaryIds = new Set(groups.primaryLines.map((line) => line.id));
  return collectOptionPrimaryComponentBlocks(system).filter((block) => primaryIds.has(block.line.id));
}
